import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './icons';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function MonthPicker({ value, onChange, style }) {
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(() => value.getFullYear());
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  useEffect(() => {
    setViewYear(value.getFullYear());
  }, [value]);

  useEffect(() => {
    const handler = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        popoverRef.current && !popoverRef.current.contains(e.target)
      ) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleOpen = () => {
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPopoverPos({ top: rect.bottom + 8, left: rect.left });
      setViewYear(value.getFullYear());
    }
    setOpen((o) => !o);
  };

  const selectMonth = (monthIndex) => {
    onChange(new Date(viewYear, monthIndex, 1));
    setOpen(false);
  };

  const now = new Date();
  const isCurrentMonth = value.getMonth() === now.getMonth() && value.getFullYear() === now.getFullYear();

  const popover = open && createPortal(
    <div ref={popoverRef} style={{
      position: 'fixed',
      top: popoverPos.top,
      left: popoverPos.left,
      zIndex: 9999,
      padding: 16,
      borderRadius: 16,
      background: 'var(--cal-bg)',
      border: '1px solid var(--line)',
      boxShadow: 'var(--shadow-lg)',
      minWidth: 260,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <button type="button" onClick={() => setViewYear((y) => y - 1)} className="pressable iconwrap" style={{ width: 28, height: 28, borderRadius: 8, border: '1px solid var(--line)', background: 'var(--surface-2)', color: 'var(--ink)' }}>
          <Icon name="chevL" size={14} />
        </button>
        <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--ink)' }}>{viewYear}</span>
        <button type="button" onClick={() => setViewYear((y) => y + 1)} className="pressable iconwrap" style={{ width: 28, height: 28, borderRadius: 8, border: '1px solid var(--line)', background: 'var(--surface-2)', color: 'var(--ink)' }}>
          <Icon name="chevR" size={14} />
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
        {MONTHS.map((label, i) => {
          const isSelected = value.getMonth() === i && value.getFullYear() === viewYear;
          const isThisMonth = i === now.getMonth() && viewYear === now.getFullYear();
          return (
            <button
              key={label}
              type="button"
              onClick={() => selectMonth(i)}
              className="pressable"
              style={{
                padding: '10px 0',
                borderRadius: 8,
                border: isThisMonth && !isSelected ? '1px solid color-mix(in srgb, var(--accent) 35%, transparent)' : '1px solid transparent',
                background: isSelected ? 'var(--accent)' : isThisMonth ? 'var(--accent-soft)' : 'transparent',
                color: isSelected ? 'var(--bg)' : isThisMonth ? 'var(--accent)' : 'var(--ink-2)',
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {!isCurrentMonth && (
        <button
          type="button"
          onClick={() => {
            onChange(new Date(now.getFullYear(), now.getMonth(), 1));
            setOpen(false);
          }}
          className="chip pressable"
          style={{ marginTop: 12, width: '100%', justifyContent: 'center' }}
        >
          This month
        </button>
      )}
    </div>,
    document.body,
  );

  return (
    <div ref={triggerRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={handleOpen}
        className="pressable"
        aria-label="Select month"
        aria-expanded={open}
        style={style}
      >
        {value.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        <Icon name="chevD" size={13} sw={2} style={{ color: 'var(--ink-3)' }} />
      </button>
      {popover}
    </div>
  );
}
